import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';
import { FileText, Printer, Search, X, RotateCcw, Ban } from 'lucide-react';
import toast from 'react-hot-toast';
import { posApi } from '@/services/api';
import Receipt from '@/components/pos/Receipt';
import { formatMoney } from '@/utils/currency';
import { translateInvoiceStatus } from '@/utils/translateStatus';

interface TodayInvoice {
  name: string;
  customer: string;
  customer_name: string;
  invoice_date: string;
  creation?: string;
  status: string;
  net_total: number;
  paid_amount: number;
  balance_amount: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onReturnInvoice?: (invoiceId: string) => void;
  canReturn?: boolean;
}

export default function TodaySalesModal({ isOpen, onClose, onReturnInvoice, canReturn = false }: Props) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [duration, setDuration] = useState('Today');
  const [reprintInvoice, setReprintInvoice] = useState<string | null>(null);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['todayCashierInvoices', duration, search],
    queryFn: async () => {
      const res = await posApi.getTodayCashierInvoices({ search, duration });
      return res.data.message as { invoices: TodayInvoice[]; date: string; count: number };
    },
    enabled: isOpen,
    refetchInterval: isOpen ? 60_000 : false,
  });

  useEffect(() => {
    if (isOpen) {
      refetch();
    }
  }, [isOpen, refetch]);

  const invoices = data?.invoices || [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return invoices;
    return invoices.filter((inv) => {
      const hay = `${inv.name} ${inv.customer_name || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [invoices, search]);

  const cancelMutation = useMutation({
    mutationFn: posApi.cancelPOSInvoice,
    onSuccess: () => {
      toast.success(t('common.cancelled'));
      refetch();
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to cancel invoice');
    }
  });

  const handleCancel = (invoiceId: string) => {
    if (window.confirm(t('todaySales.confirmCancel', 'Are you sure you want to cancel this invoice?'))) {
      cancelMutation.mutate(invoiceId);
    }
  };

  const handleClose = () => {
    setSearch('');
    setReprintInvoice(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
        <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
          <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-100 shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 bg-primary-50 text-primary-600 rounded-lg flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-gray-900 text-base sm:text-lg">{t('todaySales.title')}</h3>
                <p className="text-sm text-gray-500 truncate">
                  {t('todaySales.subtitle', { count: filtered.length > 0 ? filtered.length : 0 })}
                </p>
              </div>
            </div>
            <button type="button" onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors shrink-0">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          <div className="px-4 pt-4 pb-2 border-b border-gray-100 bg-gray-50/50 flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="input-field pl-9"
                placeholder={t('todaySales.searchPlaceholder')}
              />
            </div>
            <select
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="input-field w-[180px]"
            >
              <option value="Today">Today</option>
              <option value="Yesterday">Yesterday</option>
              <option value="This Week">This Week</option>
              <option value="This Month">This Month</option>
              <option value="All">All Time</option>
            </select>
          </div>

          <div className="flex-1 overflow-y-auto p-4 sm:p-5">
            {isLoading || isFetching ? (
              <div className="flex items-center justify-center h-40">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                {search.trim() ? t('todaySales.noMatch') : t('todaySales.empty')}
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                {/* Header row (desktop) */}
                <div className="hidden sm:grid grid-cols-[minmax(0,1.4fr)_1.2fr_0.8fr_0.8fr_0.9fr_auto] gap-3 px-3 py-2 bg-gray-50 border-b border-gray-100 text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                  <div>{t('todaySales.invoice')}</div>
                  <div>{t('todaySales.customer')}</div>
                  <div>{t('todaySales.time')}</div>
                  <div>{t('common.status')}</div>
                  <div className="text-right">{t('todaySales.total')}</div>
                  <div className="min-w-[80px]" />
                </div>

                <div className="divide-y divide-gray-100">
                  {filtered.map((inv) => {
                    const when = inv.creation || inv.invoice_date;
                    return (
                      <div
                        key={inv.name}
                        className="bg-white px-3 py-2.5 hover:bg-gray-50 transition-colors"
                      >
                        {/* Desktop */}
                        <div className="hidden sm:grid grid-cols-[minmax(0,1.4fr)_1.2fr_0.8fr_0.8fr_0.9fr_auto] gap-3 items-center">
                          <div className="min-w-0">
                            <span className="font-medium text-gray-900 text-sm truncate">{inv.name}</span>
                          </div>
                          
                          <div className="text-sm text-gray-600 truncate">
                            {inv.customer_name || t('todaySales.walkIn')}
                          </div>
                          
                          <div className="text-sm text-gray-600">
                            {when ? format(new Date(when), 'hh:mm a') : '-'}
                          </div>

                          <div>
                            <span
                              className={`inline-block px-1.5 py-0.5 text-[10px] font-medium rounded ${
                                inv.status === 'Paid'
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : inv.status === 'Partially Paid'
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : 'bg-red-100 text-red-700'
                              }`}
                            >
                              {translateInvoiceStatus(inv.status, t)}
                            </span>
                          </div>

                          <div className="text-sm font-medium text-gray-900 text-right">
                            {formatMoney(Number(inv.net_total || 0))}
                          </div>

                          <div className="flex items-center justify-end gap-1.5 shrink-0 flex-nowrap min-w-[120px]">
                            {inv.status !== 'Cancelled' && inv.status !== 'Returned' && (
                              <button
                                type="button"
                                title={t('todaySales.cancelInvoice', 'Cancel Invoice')}
                                onClick={() => handleCancel(inv.name)}
                                disabled={cancelMutation.isPending}
                                className="inline-flex items-center justify-center p-1.5 rounded bg-red-50 text-red-700 hover:bg-red-100 transition-colors disabled:opacity-50"
                              >
                                <Ban className="w-4 h-4" />
                              </button>
                            )}
                            {canReturn && onReturnInvoice && inv.status !== 'Returned' && inv.status !== 'Cancelled' && (
                              <button
                                type="button"
                                title={t('todaySales.returnInvoice')}
                                onClick={() => {
                                  onReturnInvoice(inv.name);
                                }}
                                className="inline-flex items-center justify-center p-1.5 rounded bg-orange-50 text-orange-700 hover:bg-orange-100 transition-colors"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              type="button"
                              title={t('todaySales.reprintInvoice')}
                              onClick={() => setReprintInvoice(inv.name)}
                              className="inline-flex items-center justify-center p-1.5 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                            >
                              <Printer className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Mobile */}
                        <div className="sm:hidden">
                          <div className="grid grid-cols-[1fr_auto] items-start gap-2">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="font-medium text-gray-900 text-sm truncate">{inv.name}</span>
                                <span
                                  className={`shrink-0 px-1.5 py-0.5 text-[10px] font-medium rounded ${
                                    inv.status === 'Paid'
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : inv.status === 'Partially Paid'
                                        ? 'bg-yellow-100 text-yellow-700'
                                        : 'bg-red-100 text-red-700'
                                  }`}
                                >
                                  {translateInvoiceStatus(inv.status, t)}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0 flex-nowrap">
                              {inv.status !== 'Cancelled' && inv.status !== 'Returned' && (
                                <button
                                  type="button"
                                  title={t('todaySales.cancelInvoice', 'Cancel Invoice')}
                                  onClick={() => handleCancel(inv.name)}
                                  disabled={cancelMutation.isPending}
                                  className="inline-flex items-center justify-center p-1.5 rounded bg-red-50 text-red-700 hover:bg-red-100 transition-colors disabled:opacity-50"
                                >
                                  <Ban className="w-4 h-4" />
                                </button>
                              )}
                              {canReturn && onReturnInvoice && inv.status !== 'Returned' && inv.status !== 'Cancelled' && (
                                <button
                                  type="button"
                                  title={t('todaySales.returnInvoice')}
                                  onClick={() => {
                                    onReturnInvoice(inv.name);
                                  }}
                                  className="inline-flex items-center justify-center p-1.5 rounded bg-orange-50 text-orange-700 hover:bg-orange-100 transition-colors"
                                >
                                  <RotateCcw className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                type="button"
                                title={t('todaySales.reprintInvoice')}
                                onClick={() => setReprintInvoice(inv.name)}
                                className="inline-flex items-center justify-center p-1.5 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                              >
                                <Printer className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          
                          <div className="mt-1 grid grid-cols-[1fr_auto] gap-2 text-xs">
                            <div>
                              <div className="text-gray-500 truncate">{inv.customer_name || t('todaySales.walkIn')}</div>
                              <div className="text-gray-400 mt-0.5">
                                {when ? format(new Date(when), 'hh:mm a') : '-'}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-medium text-gray-900 mt-1">
                                {formatMoney(Number(inv.net_total || 0))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {reprintInvoice && (
        <Receipt
          invoiceName={reprintInvoice}
          onClose={() => setReprintInvoice(null)}
        />
      )}
    </>
  );
}
